import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import moment from 'moment-timezone'
import ReactToPrint from 'react-to-print'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faFolderOpen,
  faMagnifyingGlassPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import PrimaryButton from '../components/Button/PrimaryButton'
import TypeBadge from '../components/TypeBadge'
import { generateHtmlDocument } from '../utils/exporter'
import { UserContext } from '../context/UserContext'
import {
  getRegistrationFile,
  postUnpproveRegistration,
  postUnverifyRegistration,
} from '../services/dashboard.service'
import PreviewModal from '../components/Modal/PreviewModal'
import { getApiErrorMessage } from '../utils/error'
import FilePreview from '../components/FilePreview'
import { Skeleton } from '../components/Skeleton'
import { statusToText, toTitleCase } from '../utils/formatter'

const MODAL = {
  NULL: '',
  KTP: 'KTP',
  OLD_KTA: 'OLD_KTA',
  PROFILE: 'PROFILE',
}

const FILE_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  DONE: 'done',
  ERROR: 'error',
}

// Warnanya saja yang didefinisikan di sini. Labelnya diambil dari
// statusToText, sumber yang sama dengan filter tab dan export -- kalau aturannya
// disalin ke sini, badge dan tab bisa tidak sepakat untuk baris yang sama.
const STATUS_STYLE = {
  'Belum verifikasi': { color: '#8A8A90', bg: '#F1F1F3' },
  Ditolak: { color: '#B42318', bg: '#FEE4E2' },
  'Verif oleh Korwil': { color: '#B54708', bg: '#FEF0C7' },
  Disetujui: { color: '#027A48', bg: '#D1FADF' },
}

const statusOf = (data) => {
  const label = data ? statusToText(data) : ''
  const style = STATUS_STYLE[label]
  return style ? { label, ...style } : null
}

const Row = ({ label, value, wide }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <dt className="text-[12px] text-muted">{label}</dt>
    <dd className="text-sm text-ink break-words">{value || '-'}</dd>
  </div>
)

const Section = ({ title, children }) => (
  <div>
    <div className="text-[12px] font-semibold uppercase tracking-wide text-muted mb-3">
      {title}
    </div>
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">{children}</dl>
  </div>
)

// Kartu berkas: isinya langsung terlihat, klik hanya untuk memperbesar.
const FileCard = ({ label, src, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group w-full rounded-xl border border-line bg-white overflow-hidden text-left hover:border-accent transition-colors"
  >
    <div className="h-[150px] flex items-center justify-center bg-subtle overflow-hidden">
      <FilePreview src={src} height={150} width={190} />
    </div>
    <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-line">
      <span className="text-[13px] font-medium text-ink truncate">{label}</span>
      <FontAwesomeIcon
        icon={faMagnifyingGlassPlus}
        className="text-[12px] text-muted group-hover:text-accent flex-none"
      />
    </div>
  </button>
)

const KtaNumbers = ({ data }) => {
  if (data?.type === 'NEW' || data?.type === 'MANUAL NEW') {
    return <Row label="No. KTA" value={data?.ktaNumber} />
  }
  if (data?.type === 'RENEW' || data?.type === 'MANUAL RENEW') {
    return (
      <>
        <Row label="KTA Lama" value={data?.oldKtaNumber} />
        <Row label="KTA Baru" value={data?.KtaNumber} />
      </>
    )
  }
  if (data?.type === 'ACTIVE') {
    return <Row label="No. KTA" value={data?.oldKtaNumber} />
  }
  return null
}

const PendaftarDetailModal = ({ open, onClose, data, onUpdated = () => {} }) => {
  const [file, setFile] = useState(null)
  const [fileStatus, setFileStatus] = useState(FILE_STATUS.IDLE)
  const [modal, setModal] = useState(MODAL.NULL)
  const { user } = useContext(UserContext) ?? {}

  const html = useMemo(() => {
    if (file) {
      return generateHtmlDocument({
        data: {
          ...data,
          profileImage: file?.profileImage,
          ktpImage: file?.ktpImage,
          oldKtaImage: file?.oldKtaImage,
        },
      })
    }
    return null
  }, [data, file])
  const pdfRef = useRef()

  const onUnverify = async () => {
    try {
      await postUnverifyRegistration(data?.id)
      toast.success('Pembatalan verifikasi sukses')
      onUpdated(data?.id, { korwilApproval: null })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Gagal membatalkan verifikasi'))
    }
  }

  const onUnapprove = async () => {
    try {
      await postUnpproveRegistration(data?.id)
      toast.success('Pembatalan persetujuan sukses')
      onUpdated(data?.id, { centralApproval: null })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Gagal membatalkan persetujuan'))
    }
  }

  useEffect(() => {
    if (!data?.id) {
      return
    }
    let ignore = false
    const fetch = async () => {
      setFile(null)
      setFileStatus(FILE_STATUS.LOADING)
      try {
        const response = await getRegistrationFile(data?.id)
        if (!ignore) {
          setFile(response?.data)
          setFileStatus(FILE_STATUS.DONE)
        }
      } catch (error) {
        // Berkas gagal dimuat tidak boleh menggantung sebagai skeleton terus.
        if (!ignore) {
          setFileStatus(FILE_STATUS.ERROR)
        }
      }
    }
    fetch()
    return () => {
      ignore = true
    }
  }, [data?.id])

  const status = statusOf(data)
  const attachments = [
    { key: MODAL.PROFILE, label: 'Foto Profil', src: file?.profileImage },
    { key: MODAL.KTP, label: 'KTP', src: file?.ktpImage },
    { key: MODAL.OLD_KTA, label: 'KTA Lama', src: file?.oldKtaImage },
  ].filter((attachment) => !!attachment.src)

  const activeAttachment = attachments.find(
    (attachment) => attachment.key === modal,
  )

  return (
    <>
      <div className={`modal ${open ? 'modal-open' : ''}`}>
        <div className="modal-box relative max-w-4xl p-0 rounded-card overflow-hidden">
          <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-line">
            <div className="min-w-0">
              <div className="text-[13px] text-muted">
                {data?.registrationNumber ?? '-'}
              </div>
              <h3 className="text-xl font-bold text-ink truncate">
                {toTitleCase(data?.fullname)}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <TypeBadge type={data?.type} />
                {status && (
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-medium"
                    style={{ backgroundColor: status.bg, color: status.color }}
                  >
                    {status.label}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3 flex-none">
              {data?.registrationNumber && (
                <div className="hidden sm:block p-2 rounded-xl border border-line bg-white">
                  <QRCode value={data.registrationNumber} size={56} />
                </div>
              )}
              <button
                aria-label="Tutup"
                className="w-8 h-8 rounded-lg border border-line text-muted hover:bg-subtle hover:text-ink transition-colors"
                onClick={onClose}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div className="max-h-[68vh] overflow-y-auto px-6 py-5 grid gap-6 lg:grid-cols-[210px_1fr]">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-muted mb-3">
                Berkas
              </div>
              <div className="flex flex-col gap-3">
                {fileStatus === FILE_STATUS.LOADING &&
                  [0, 1].map((i) => (
                    <Skeleton key={i} className="h-[186px] w-full rounded-xl" />
                  ))}
                {fileStatus === FILE_STATUS.ERROR && (
                  <div className="rounded-xl border border-line bg-subtle px-3 py-6 text-center text-[13px] text-muted">
                    Gagal memuat berkas.
                  </div>
                )}
                {fileStatus === FILE_STATUS.DONE && attachments.length === 0 && (
                  <div className="rounded-xl border border-line bg-subtle px-3 py-6 text-center text-[13px] text-muted">
                    <FontAwesomeIcon
                      icon={faFolderOpen}
                      className="text-lg block mb-2"
                    />
                    Tidak ada berkas terlampir.
                  </div>
                )}
                {attachments.map((attachment) => (
                  <FileCard
                    key={attachment.key}
                    label={attachment.label}
                    src={attachment.src}
                    onOpen={() => setModal(attachment.key)}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <Section title="Data Pendaftar">
                <KtaNumbers data={data} />
                <Row
                  label="Waktu Pendaftaran"
                  value={
                    data?.createdAt
                      ? moment(data.createdAt)
                          .tz('Asia/Jakarta')
                          .format('D MMM YYYY HH:mm:ss')
                      : '-'
                  }
                />
                <Row label="Nomor Identitas (NIK)" value={data?.nik} />
                <Row
                  label="Umur"
                  value={
                    data?.birthDate
                      ? `${moment().diff(moment(data.birthDate), 'years')} tahun`
                      : '-'
                  }
                />
                <Row label="Ukuran Kaos" value={data?.shirtSize} />
                <Row label="Email" value={data?.email} />
                <Row label="Nomor Telepon" value={data?.phoneNumber} />
              </Section>

              <Section title="Alamat">
                <Row label="Alamat" value={data?.address} wide />
                <Row label="Provinsi" value={data?.province} />
                <Row label="Kotamadya/Kabupaten" value={data?.city} />
                <Row label="Kecamatan" value={data?.district} />
                <Row label="Kelurahan/Desa" value={data?.subdistrict} />
                <Row label="Korwil" value={data?.korwil?.name} />
              </Section>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-line bg-subtle">
            {['PP', 'Master'].includes(user?.role?.name) &&
              data?.centralApproval && (
                <button
                  className="h-10 px-4 rounded-xl border border-line bg-white text-sm font-semibold text-ink hover:bg-subtle transition-colors"
                  onClick={onUnapprove}
                >
                  Batalkan Persetujuan
                </button>
              )}
            {['Korwil', 'Master'].includes(user?.role?.name) &&
              !data?.centralApproval &&
              data?.korwilApproval && (
                <button
                  className="h-10 px-4 rounded-xl border border-line bg-white text-sm font-semibold text-ink hover:bg-subtle transition-colors"
                  onClick={onUnverify}
                >
                  Batalkan Verifikasi
                </button>
              )}
            {file && (
              <ReactToPrint
                content={() => pdfRef.current}
                trigger={() => (
                  <PrimaryButton>
                    <FontAwesomeIcon icon={faDownload} />
                    Download PDF
                  </PrimaryButton>
                )}
              />
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: '100vw',
        }}
      >
        <div ref={pdfRef} dangerouslySetInnerHTML={{ __html: html }}></div>
      </div>

      <PreviewModal
        open={!!activeAttachment}
        title={activeAttachment?.label}
        src={activeAttachment?.src}
        onClose={() => setModal(MODAL.NULL)}
      />
    </>
  )
}

export default PendaftarDetailModal
